import { BadInputError } from '@watershed/errors/BadInputError';
import {
  RawSQL,
  /* eslint-disable @watershed/no-dangerous-raw-sql -- DuckDB usage only */
  dangerousRawSql,
  sql,
  sqlF,
} from '../../sqlTemplate';
import { ASTCall } from '../ast';
import { ExprType, stringifyType } from '../expr_types';
import { traceHelper } from '../sql/internal';
import { TypeConversionError, convertToType } from '../typeConversion';
import {
  TypecheckWarning,
  assertArgShape,
  assertKwargShape,
  typecheckExpression,
} from '../typecheck';
import { Context, Func, SqlExpression } from '../types';
import { safeMaxStage } from '../utils';
import { bestCompatibleType } from '../utils/bestCompatibleType';

// See unit tests in `lscriptEval/__tests__/iferror.test.ts`.

// Usage: iferror(expr, exprIfError)
// c.f. the IFERROR(...) function in Excel
export const iferror: Func = {
  typecheck(
    ctx: Context,
    call: ASTCall,
    warnings?: Array<TypecheckWarning>
  ): ExprType {
    assertArgShape(call, 2, 2);
    assertKwargShape(call, [], false);

    const mainType = typecheckExpression(ctx, call.args[0], warnings);
    const errType = typecheckExpression(ctx, call.args[1], warnings);
    const outType = bestCompatibleType(mainType, errType);

    try {
      convertToType(ctx, { fromExpr: call.args[0], toType: outType });
      convertToType(ctx, { fromExpr: call.args[1], toType: outType });
    } catch (error: any) {
      if (error instanceof TypeConversionError) {
        throw new BadInputError(
          `Arguments to \`iferror\` must be convertible to a common type: no common type is possible between argument types "${stringifyType(
            mainType
          )}" and "${stringifyType(errType)}"`
        );
      }
      throw error;
    }

    return outType;
  },

  eval(ctx: Context, call: ASTCall): unknown {
    const outType = typecheckExpression(ctx, call);

    try {
      return convertToType(ctx, {
        fromExpr: call.args[0],
        toType: outType,
      }).eval();
    } catch {
      return convertToType(ctx, {
        fromExpr: call.args[1],
        toType: outType,
      }).eval();
    }
  },

  toSql(ctx: Context, call: ASTCall): SqlExpression {
    const outType = typecheckExpression(ctx, call);

    const exprSql = convertToType(ctx, {
      fromExpr: call.args[0],
      toType: outType,
    }).toSql();
    const exprIfErrorSql = convertToType(ctx, {
      fromExpr: call.args[1],
      toType: outType,
    }).toSql();

    if (exprSql.error == null) {
      return exprSql;
    }

    const error = exprSql.error;
    function branch(cb: (sql: SqlExpression) => RawSQL): RawSQL {
      return sqlF`CASE WHEN ${dangerousRawSql(error.exists)} THEN ${cb(
        exprIfErrorSql
      )} ELSE ${cb(exprSql)} END`;
    }

    return traceHelper(
      outType,
      {
        expr: sql`${branch((s) => dangerousRawSql(s.expr))}`,
        stage: safeMaxStage([exprSql, exprIfErrorSql]),
        unitExpr: exprSql.unitExpr,
        error: exprIfErrorSql.error
          ? {
              exists: `(${exprSql.error.exists} AND ${exprIfErrorSql.error.exists})`,
              expr: exprIfErrorSql.error.expr,
            }
          : null,
      },
      (sub) => branch((s) => sub(s.trace))
    );
  },
};
